import { useEffect, useState } from "react";
import {
  listMyAdminTrails,
  setStatusOverride,
  ApiError,
  type AdminAssignment,
} from "../api";

/**
 * Daily-driver quick action surfaced in the DashboardShell's top bar.
 * Curators-with-trails + admins both hit this — it's the primary
 * trail-day workflow ("trail's wet, mark it Caution") so it lives in
 * the top chrome on every section, not nested behind a sidebar route.
 *
 * Flow:
 *   1. Pill in the top bar.
 *   2. Click → small dropdown:
 *        - "All your trails (N)" → multi-trail push
 *        - each individual trail → single-trail push
 *   3. Pick → modal with status picker + optional TTL + (multi only)
 *      a per-trail checkbox list pre-checked.
 *   4. Submit → loops setStatusOverride → success toast → close.
 *
 * Hidden when the user has zero admin trails. Fetches the list once
 * on mount; no real-time refresh.
 */

/** UI-side status options. Mapped to the worker's wire enum
 *  (`open | partially_open | closed | unknown`) at submit time. */
type Status = "open" | "caution" | "closed";

const STATUS_OPTIONS: { value: Status; label: string; color: string }[] = [
  { value: "open", label: "Open", color: "#2EC75C" },
  { value: "caution", label: "Caution", color: "#FFCC00" },
  { value: "closed", label: "Closed", color: "#FF3B30" },
];

const STATUS_TO_WIRE: Record<Status, "open" | "partially_open" | "closed"> = {
  open: "open",
  caution: "partially_open",
  closed: "closed",
};

type ModalTarget =
  | { kind: "single"; trail: AdminAssignment }
  | { kind: "multi"; trails: AdminAssignment[] };

export function SetStatusPill() {
  const [trails, setTrails] = useState<AdminAssignment[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [modal, setModal] = useState<ModalTarget | null>(null);

  useEffect(() => {
    listMyAdminTrails()
      .then((r) => setTrails(r.items))
      .catch(() => setTrails([]));
  }, []);

  if (trails === null) {
    return <span className="set-status-pill loading">Loading…</span>;
  }
  if (trails.length === 0) return null;

  return (
    <>
      <div className="set-status-wrap">
        <button
          type="button"
          className="set-status-pill"
          onClick={() => setPickerOpen((v) => !v)}
        >
          <span className="set-status-dot" aria-hidden="true" />
          Set status
          <span className="set-status-chevron" aria-hidden="true">▾</span>
        </button>
        {pickerOpen && (
          <div className="set-status-dropdown" role="menu">
            {/* "All trails" sits above an individual-trail list when
                the admin has multiple trails. With a single trail it's
                redundant, so skip it. */}
            {trails.length > 1 && (
              <>
                <button
                  type="button"
                  className="set-status-dropdown-item set-status-dropdown-all"
                  onClick={() => {
                    setModal({ kind: "multi", trails });
                    setPickerOpen(false);
                  }}
                >
                  <div className="set-status-dropdown-name">
                    All your trails ({trails.length})
                  </div>
                  <div className="set-status-dropdown-sub">
                    Push the same status to every trail you manage
                  </div>
                </button>
                <div className="set-status-dropdown-sep" aria-hidden="true" />
              </>
            )}
            {trails.map((t) => (
              <button
                key={t.slug}
                type="button"
                className="set-status-dropdown-item"
                onClick={() => {
                  setModal({ kind: "single", trail: t });
                  setPickerOpen(false);
                }}
              >
                <div className="set-status-dropdown-name">{t.name}</div>
                {(t.region || t.state) && (
                  <div className="set-status-dropdown-sub">
                    {[t.region, t.state].filter(Boolean).join(" · ")}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <SetStatusModal target={modal} onClose={() => setModal(null)} />
      )}
    </>
  );
}

function SetStatusModal({
  target,
  onClose,
}: {
  target: ModalTarget;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<Status>("open");
  const [summary, setSummary] = useState("");
  const [ttlHours, setTtlHours] = useState<number>(4);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Multi-trail mode pre-selects every trail. Admin can untick to
  // narrow scope; defaults match "push to all" intent.
  const allSlugs =
    target.kind === "multi" ? target.trails.map((t) => t.slug) : [];
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(
    () => new Set(allSlugs)
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function toggleSlug(slug: string) {
    setSelectedSlugs((cur) => {
      const next = new Set(cur);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  const targets: AdminAssignment[] =
    target.kind === "single"
      ? [target.trail]
      : target.trails.filter((t) => selectedSlugs.has(t.slug));

  async function submit() {
    if (targets.length === 0) return;
    setSubmitting(true);
    setError(null);
    setProgress(targets.length > 1 ? { done: 0, total: targets.length } : null);
    const failures: string[] = [];
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      try {
        await setStatusOverride({
          slug: t.slug,
          status: STATUS_TO_WIRE[status],
          summary: summary.trim() || undefined,
          ttlHours,
        });
      } catch (err) {
        failures.push(
          `${t.name}: ${err instanceof ApiError ? err.message : "failed"}`
        );
      }
      setProgress(
        targets.length > 1 ? { done: i + 1, total: targets.length } : null
      );
    }
    setProgress(null);
    setSubmitting(false);
    if (failures.length === 0) {
      setDone(true);
      setTimeout(onClose, 1100);
    } else {
      setError(
        `${failures.length} of ${targets.length} failed. ${failures[0]}`
      );
    }
  }

  const headline =
    target.kind === "single"
      ? target.trail.name
      : `${targets.length} of ${target.trails.length} trails`;

  return (
    <div className="set-status-modal-backdrop" onClick={onClose}>
      <div
        className="set-status-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={
          target.kind === "single"
            ? `Set status for ${target.trail.name}`
            : "Set status for multiple trails"
        }
      >
        <header className="set-status-modal-head">
          <div>
            <div className="muted" style={{ fontSize: 12 }}>{headline}</div>
            <h3 style={{ margin: 0 }}>Set status</h3>
          </div>
          <button
            type="button"
            className="secondary small"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="set-status-modal-body">
          <div className="set-status-options">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`set-status-option ${status === opt.value ? "is-active" : ""}`}
                style={{ ["--opt-color" as never]: opt.color }}
                onClick={() => setStatus(opt.value)}
                aria-pressed={status === opt.value}
              >
                <span className="set-status-option-dot" />
                {opt.label}
              </button>
            ))}
          </div>

          <label className="drawer-field">
            <span className="drawer-field-label">Summary (optional)</span>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="e.g. Wet from last night's rain"
              maxLength={200}
            />
          </label>

          <label className="drawer-field">
            <span className="drawer-field-label">Hold for</span>
            <select
              value={ttlHours}
              onChange={(e) => setTtlHours(Number(e.target.value))}
            >
              <option value={1}>1 hour</option>
              <option value={4}>4 hours</option>
              <option value={12}>12 hours</option>
              <option value={24}>1 day</option>
              <option value={48}>2 days</option>
              <option value={72}>3 days (max)</option>
            </select>
            <span className="drawer-field-hint">
              How long your override holds before the cron resumes
              auto-classifying.
            </span>
          </label>

          {target.kind === "multi" && (
            <div className="drawer-field">
              <span className="drawer-field-label">Trails to update</span>
              <div
                style={{
                  margin: "4px 0 0",
                  maxHeight: 180,
                  overflowY: "auto",
                  border: "1px solid #2b2b22",
                  borderRadius: 6,
                  background: "#1d1d17",
                }}
              >
                {target.trails.map((t, idx) => {
                  const meta = [t.region, t.state].filter(Boolean).join(" · ");
                  return (
                    <div
                      key={t.slug}
                      onClick={() => !submitting && toggleSlug(t.slug)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        cursor: submitting ? "default" : "pointer",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: 14,
                        color: "#f3f1e5",
                        borderTop: idx > 0 ? "1px solid #2b2b22" : "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSlugs.has(t.slug)}
                        onChange={() => toggleSlug(t.slug)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={submitting}
                        style={{ flex: "0 0 auto", width: "auto" }}
                      />
                      <span style={{ fontWeight: 600, color: "#f3f1e5" }}>
                        {t.name}
                      </span>
                      {meta && (
                        <span style={{ color: "#a8a698", fontSize: 12 }}>
                          {meta}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <span className="drawer-field-hint">
                Untick any you don't want to push this round.
              </span>
            </div>
          )}

          {error && <div className="error">{error}</div>}
          {done && (
            <div className="success">
              {target.kind === "multi"
                ? `Status pushed to ${targets.length} trails. Riders notified.`
                : "Status updated. Riders pushed."}
            </div>
          )}
        </div>

        <footer className="set-status-modal-foot">
          <button type="button" className="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || done || targets.length === 0}
          >
            {submitting
              ? progress
                ? `Pushing ${progress.done}/${progress.total}…`
                : "Setting…"
              : done
                ? "Done"
                : target.kind === "multi"
                  ? `Push to ${targets.length} trail${targets.length === 1 ? "" : "s"}`
                  : "Set status"}
          </button>
        </footer>
      </div>
    </div>
  );
}
